// src/utils/name.js
// Single source of truth for GeoJSON property-name cleaning.

/**
 * cleanName(raw)
 * – Input may be null, undefined, number, or mixed string.
 * – Removes \r, \n, repeated tokens, commas, semicolons, pipes.
 * – Takes first meaningful token for display.
 * – Collapses multi-space to single space, trims.
 * – Returns single-line cleaned string.
 */
export const cleanName = (val) => {
  if (val === undefined || val === null) return "";
  const s = String(val);
  // Split by newlines / commas / semicolons / pipes and take first meaningful token
  const parts = s
    .split(/[\r\n,;|]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const first = parts.length ? parts[0] : s.trim();
  return first.replace(/\s+/g, " ").trim();
};

/**
 * canonicalName(raw)
 * – cleanName(raw).toLowerCase()
 * – Used for all internal matching / dedup keys.
 */
export const canonicalName = (val) => cleanName(val).toLowerCase();

/**
 * prettyName(raw)
 * – Keeps casing of first token, single-line, collapsed spaces.
 * – Used for labels / UI display and WMS CQL_FILTER values.
 */
export const prettyName = (val) => {
  if (val === undefined || val === null) return "";
  const s = String(val);
  const parts = s
    .split(/[\r\n,;|]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length ? parts[0].replace(/\s+/g, " ") : s.trim();
};

/**
 * normalizeName(s)
 * – Lowercase + trim + collapse whitespace. Used for matching feature names.
 */
export const normalizeName = (s) => {
  if (s === undefined || s === null) return "";
  return String(s).toLowerCase().trim().replace(/\s+/g, " ");
};

/**
 * extractProp(props, ...keys)
 * – Safely extracts the first existing property from a list of keys.
 */
export const extractProp = (props, ...keys) => {
  if (!props) return null;
  for (const k of keys) {
    if (props[k] !== undefined && props[k] !== null) return props[k];
  }
  return null;
};

/**
 * getDistrictName(feature)
 * – Reads district name from a GeoJSON feature, checking multiple property keys.
 */
export const getDistrictName = (feature) => {
  const props = feature?.properties || {};
  const raw = extractProp(props, "district", "District", "NAME_2", "dist", "DISTRICT");
  return raw ? cleanName(raw) : "";
};

/**
 * getTehsilName(feature)
 * – Reads tehsil/sub-district name from a GeoJSON feature.
 */
export const getTehsilName = (feature) => {
  const props = feature?.properties || {};
  const raw = extractProp(props, "tehsil", "subdist", "NAME_3", "NAME", "Tehsil", "TEHSIL");
  return raw ? cleanName(raw) : "";
};

/**
 * getVillageName(feature) 
 * – Reads village name from a GeoJSON feature.
 */
export const getVillageName = (feature) => {
  const props = feature?.properties || {};
  const raw = extractProp(props, "village", "NAME_4", "Village", "VILLAGE", "name", "Name");
  return raw ? cleanName(raw) : "";
};
